import React, { useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import DataPreviewTable from './DataPreviewTable';
import SheetPicker from './SheetPicker';
import SavedConfigPicker from './SavedConfigPicker';
import { useSelectSheet } from '../../hooks/useLeadImportApi';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import { isNotFoundError } from '../../utils/errorHelpers';

/**
 * FileAnalysisStep handles the file analysis display.
 *
 * - If the file needs sheet selection (sheets are available but no headers yet),
 *   it shows the SheetPicker.
 * - Once headers are available (either from direct analysis or after sheet selection),
 *   it shows the file info card, data preview table, saved config picker,
 *   and a "Configure new mapping" button.
 */
const FileAnalysisStep: React.FC = () => {
  const fileID = useImportWizardStore((s) => s.fileID);
  const filename = useImportWizardStore((s) => s.filename);
  const headers = useImportWizardStore((s) => s.headers);
  const sampleData = useImportWizardStore((s) => s.sampleData);
  const rowCount = useImportWizardStore((s) => s.rowCount);
  const sheets = useImportWizardStore((s) => s.sheets);
  const setAnalysisData = useImportWizardStore((s) => s.setAnalysisData);
  const setStep = useImportWizardStore((s) => s.setStep);

  const selectSheet = useSelectSheet();

  const needsSheetSelection = sheets.length > 0 && headers.length === 0;
  const isFileNotFound = isNotFoundError(selectSheet.error);

  const handleSelectSheet = useCallback(
    async (sheetName: string) => {
      if (!fileID) return;
      try {
        const result = await selectSheet.mutateAsync({
          fileID,
          sheetName,
        });
        setAnalysisData({
          headers: result.headers,
          sampleData: result.sampleData,
          rowCount: result.rowCount,
          sheets: [],
        });
      } catch {
        // Error handled by mutation onError callback
      }
    },
    [fileID, selectSheet, setAnalysisData]
  );

  const handleConfigureNewMapping = useCallback(() => {
    setStep('mapping');
  }, [setStep]);

  const handleSavedConfigSelected = useCallback(() => {
    setStep('mapping');
  }, [setStep]);

  // Determine file type display from filename extension
  const getFileType = (name: string | null): string => {
    if (!name) return 'Unknown';
    const ext = name.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'csv':
        return 'CSV';
      case 'xlsx':
        return 'Excel (XLSX)';
      case 'xls':
        return 'Excel (XLS)';
      default:
        return 'Unknown';
    }
  };

  // Show sheet picker when file needs sheet selection
  if (needsSheetSelection) {
    return (
      <Box data-testid="file-analysis-step">
        <Typography variant="h6" gutterBottom>
          File Analysis
        </Typography>

        {isFileNotFound && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            data-testid="file-not-found-error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setStep('upload')}
              >
                Back to Upload
              </Button>
            }
          >
            File not found. It may have been deleted or expired. Please upload a
            new file.
          </Alert>
        )}

        {filename && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                File
              </Typography>
              <Typography variant="body1">{filename}</Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Type
              </Typography>
              <Typography variant="body1">{getFileType(filename)}</Typography>
            </CardContent>
          </Card>
        )}

        <SheetPicker sheets={sheets} onSelectSheet={handleSelectSheet} />

        {selectSheet.isPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading sheet data...
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Show analyzed data with file info, preview, and config options
  return (
    <Box data-testid="file-analysis-step">
      <Typography variant="h6" gutterBottom>
        File Analysis
      </Typography>

      {/* File info card */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary">
            File
          </Typography>
          <Typography
            variant="body1"
            title={filename || 'Unknown'}
            sx={{
              wordBreak: 'break-all',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {filename || 'Unknown'}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Type
          </Typography>
          <Typography variant="body1">{getFileType(filename)}</Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Rows
          </Typography>
          <Typography variant="body1">{rowCount.toLocaleString()}</Typography>
        </CardContent>
      </Card>

      {/* Row limit warning */}
      {rowCount > 10000 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This file has {rowCount.toLocaleString()} rows which exceeds the
          maximum of 10,000 rows per import job. Please split the file into
          smaller batches before uploading.
        </Alert>
      )}

      {/* Data preview table */}
      <DataPreviewTable headers={headers} sampleData={sampleData} />

      {/* Saved config picker and new mapping button */}
      <Box sx={{ mt: 3 }}>
        <SavedConfigPicker onConfigSelected={handleSavedConfigSelected} />

        <Button
          variant="contained"
          onClick={handleConfigureNewMapping}
          disabled={rowCount > 10000}
          data-testid="configure-new-mapping-btn"
          sx={{ mt: 1 }}
        >
          Configure new mapping
        </Button>
      </Box>
    </Box>
  );
};

export default FileAnalysisStep;
